// Compatibility shim: Re-export from bootstrap-vue-next
// This allows existing `import { ... } from 'bootstrap-vue'` to work

import {
  BAlert as _BAlert,
  BDropdownItem as _BDropdownItem,
  BModal as _BModal,
  BOffcanvas as _BOffcanvas,
  BPagination as _BPagination,
  BPlaceholder as _BPlaceholder,
  BPlaceholderWrapper as _BPlaceholderWrapper,
  BTable as _BTable,
  BTableSimple as _BTableSimple,
} from "bootstrap-vue-next"
import { defineComponent, h, ref } from "vue"

export * from "bootstrap-vue-next"

// bootstrap-vue-next 0.15.5's BTable never forwards BTableLite's `row-dbl-clicked`
// (it only wires `onRowClicked`), so `@row-dbl-clicked` is dead on every table.
// `row-clicked` does arrive, and MouseEvent.detail === 2 marks the second click.
export const BTable = {
  name: "BTable",
  inheritAttrs: false,
  emits: ["row-dbl-clicked"],
  setup(props, { attrs, slots, emit, expose }) {
    const inner = ref(null)

    // parents reach for the selection API through $refs
    expose({
      selectAllRows: (...a) => inner.value?.selectAllRows(...a),
      clearSelected: (...a) => inner.value?.clearSelected(...a),
      selectRow: (...a) => inner.value?.selectRow(...a),
      unselectRow: (...a) => inner.value?.unselectRow(...a),
    })

    return () =>
      h(
        _BTable,
        {
          ...attrs,
          bordered: attrs.bordered ?? true,
          responsive: attrs.responsive === "" ? true : attrs.responsive,
          ref: inner,
          onRowClicked: (item, index, event) => {
            attrs.onRowClicked?.(item, index, event)
            if (event.detail === 2)
              emit("row-dbl-clicked", item, index, event)
          },
        },
        slots,
      )
  },
}

export const BTableSimple = {
  name: "BTableSimple",
  inheritAttrs: false,
  setup(_, { attrs, slots }) {
    return () =>
      h(
        _BTableSimple,
        {
          ...attrs,
          bordered: attrs.bordered ?? true,
          responsive: attrs.responsive === "" ? true : attrs.responsive,
        },
        slots,
      )
  },
}

export const BAlert = {
  name: "BAlert",
  inheritAttrs: false,
  props: {
    show: { type: [Boolean, Number], default: false },
    modelValue: { type: [Boolean, Number], default: undefined },
  },
  setup(props, { attrs, slots }) {
    return () =>
      h(
        _BAlert,
        {
          ...attrs,
          modelValue:
            props.modelValue !== undefined ? props.modelValue : props.show,
        },
        slots,
      )
  },
}

export const BDropdownItem = {
  name: "BDropdownItem",
  inheritAttrs: false,
  setup(_, { attrs, slots }) {
    return () => h(_BDropdownItem, { variant: null, ...attrs }, slots)
  },
}

export {
  BAvatar,
  BBadge,
  BBreadcrumb,
  BBreadcrumbItem,
  BButton,
  BButtonGroup,
  BCard,
  BCardBody,
  BCardFooter,
  BCardHeader,
  BCardSubtitle as BCardSubTitle,
  BCardText,
  BCardTitle,
  BCol,
  BCollapse,
  BContainer,
  BDropdown,
  BDropdownDivider,
  BForm,
  BFormCheckbox,
  BFormCheckboxGroup,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormRadio,
  BFormRadioGroup,
  BFormSelect,
  BFormSelectOption,
  BFormSpinbutton,
  BFormTextarea,
  BImg,
  BInputGroup,
  BInputGroupText as BInputGroupAppend,
  BInputGroupText as BInputGroupPrepend,
  BLink,
  BListGroup,
  BListGroupItem,
  BNav,
  BNavbar,
  BNavbarNav,
  BNavItem,
  BNavItemDropdown,
  BOffcanvas,
  BOverlay,
  BPlaceholder,
  BPlaceholderWrapper,
  BPopover,
  BProgress,
  BProgressBar,
  BRow,
  BSpinner,
  BTab,
  BTabs,
  BTbody,
  BTd,
  BTfoot,
  BTh,
  BThead,
  BToast,
  BTooltip,
  BTr,
} from "bootstrap-vue-next"

// ponytail: bootstrap-vue 2 rendered modal content only while the modal was open.
// bootstrap-vue-next renders it eagerly, so children inside modals mount before the
// parent sets their filter props and keep stale state between opens. Restore lazy.
_BModal.props.lazy.default = true
export const BModal = _BModal

// ponytail: bootstrap-vue 2 always rendered a page-1 button; bootstrap-vue-next
// renders none when totalRows is 0, leaving lone prev/next arrows on empty lists.
export const BPagination = {
  name: "BPagination",
  inheritAttrs: false,
  setup(props, { attrs, slots }) {
    return () => {
      const { totalRows, "total-rows": totalRowsKebab, ...rest } = attrs
      return h(
        _BPagination,
        { ...rest, totalRows: Number.parseInt(totalRows ?? totalRowsKebab) || 1 },
        slots,
      )
    }
  },
}

// BSkeleton / BSkeletonWrapper - replaced by BPlaceholder / BPlaceholderWrapper in Vue 3
export const BSkeleton = _BPlaceholder
export const BSkeletonWrapper = _BPlaceholderWrapper

// Toast plugin is handled differently in bootstrap-vue-next
export const ToastPlugin = {
  install() {
    // No-op - toasts are handled via useToast composable in bootstrap-vue-next
  },
}

// Modal plugin stub
export const ModalPlugin = {
  install() {
    // No-op - modals work without plugin in bootstrap-vue-next
  },
}

// BMedia was removed in Bootstrap 5 - provide stub components
// These render as simple flex divs
export const BMedia = defineComponent({
  name: "BMedia",
  props: { tag: { type: String, default: "div" }, verticalAlign: String },
  setup(props, { slots }) {
    return () => h(props.tag, { class: "d-flex" }, slots.default?.())
  },
})

export const BMediaAside = defineComponent({
  name: "BMediaAside",
  props: { tag: { type: String, default: "div" }, verticalAlign: String },
  setup(props, { slots }) {
    return () =>
      h(props.tag, { class: "flex-shrink-0 me-3" }, slots.default?.())
  },
})

export const BMediaBody = defineComponent({
  name: "BMediaBody",
  props: { tag: { type: String, default: "div" } },
  setup(props, { slots }) {
    return () => h(props.tag, { class: "flex-grow-1" }, slots.default?.())
  },
})

// Other deprecated components as stubs
export const BFormTimepicker = defineComponent({
  name: "BFormTimepicker",
  setup(_, { slots }) {
    return () => h("input", { type: "time", class: "form-control" })
  },
})

export const BFormDatepicker = defineComponent({
  name: "BFormDatepicker",
  setup(_, { slots }) {
    return () => h("input", { type: "date", class: "form-control" })
  },
})

// BCalendar - stub component (bootstrap-vue-next does not have BCalendar)
export const BCalendar = defineComponent({
  name: "BCalendar",
  props: {
    value: [String, Date],
    locale: String,
    min: [String, Date],
    max: [String, Date],
    dateDisabledFn: Function,
    startWeekday: { type: [Number, String], default: 0 },
  },
  emits: ["input", "context"],
  setup(props, { emit }) {
    return () =>
      h("input", {
        type: "date",
        class: "form-control",
        value: props.value,
        min: props.min,
        max: props.max,
        onInput: e => emit("input", e.target.value),
      })
  },
})

// BIcon - generic icon stub (renders a placeholder span)
export const BIcon = defineComponent({
  name: "BIcon",
  props: {
    icon: String,
    variant: String,
    fontScale: { type: [Number, String], default: 1 },
    scale: { type: [Number, String], default: 1 },
  },
  setup(props, { slots }) {
    return () =>
      h(
        "span",
        {
          "class": [
            "bi",
            props.icon ? `bi-${props.icon}` : "",
            props.variant ? `text-${props.variant}` : "",
          ],
          "role": "img",
          "aria-label": props.icon,
        },
        slots.default?.(),
      )
  },
})

// BIconCalculatorFill - calculator icon stub
export const BIconCalculatorFill = defineComponent({
  name: "BIconCalculatorFill",
  props: {
    scale: { type: [Number, String], default: 1 },
    fontScale: { type: [Number, String], default: 1 },
    variant: String,
  },
  setup(props) {
    const effectiveScale = Number.parseFloat(props.fontScale || props.scale) || 1
    const size = effectiveScale * 16
    return () =>
      h(
        "svg",
        {
          viewBox: "0 0 16 16",
          width: size,
          height: size,
          fill: "currentColor",
          class: [
            "bi",
            "bi-calculator-fill",
            props.variant ? `text-${props.variant}` : "",
          ],
          style: { verticalAlign: "-0.125em" },
        },
        [
          h("path", {
            d: "M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zm2 .5v2a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5h-7a.5.5 0 0 0-.5.5m0 4v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5M4.5 9a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5zM4 12.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5M7.5 6a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5zM7 9.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5m.5 2.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5zM10 6.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5m.5 2.5a.5.5 0 0 0-.5.5v4a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 0-.5-.5z",
          }),
        ],
      )
  },
})

// Bootstrap Vue icons - stub with simple SVG circle
export const BIconCircle = defineComponent({
  name: "BIconCircle",
  props: {
    scale: { type: [Number, String], default: 1 },
    variant: String,
  },
  setup(props) {
    const size = Number.parseFloat(props.scale) * 16
    return () =>
      h(
        "svg",
        {
          viewBox: "0 0 16 16",
          width: size,
          height: size,
          fill: "currentColor",
          class: [
            "bi",
            "bi-circle",
            props.variant ? `text-${props.variant}` : "",
          ],
        },
        [
          h("circle", {
            "cx": 8,
            "cy": 8,
            "r": 7,
            "fill": "none",
            "stroke": "currentColor",
            "stroke-width": 2,
          }),
        ],
      )
  },
})

// BSidebar - maps to BOffcanvas in bootstrap-vue-next
// In Bootstrap 5, sidebars are replaced by Offcanvas.
// bootstrap-vue 2 API'si BOffcanvas'da boshqacha, shu yerda o'giramiz:
//  - `right`  -> placement="end" (aks holda panel chapdan chiqadi)
//  - `width`  -> inline style (BOffcanvas'da bunday prop yo'q, .offcanvas-end 400px)
//  - `backdrop` bo'sh atribut sifatida "" bo'lib keladi, BOffcanvas esa qat'iy
//    `=== true` ni tekshiradi, ya'ni fon qoraytirilmay qolardi
export const BSidebar = defineComponent({
  name: "BSidebar",
  inheritAttrs: false,
  props: {
    right: { type: [Boolean, String], default: false },
    width: { type: String, default: undefined },
    // BOffcanvas default'i true - o'zgartirmaymiz, faqat "" ni true ga o'giramiz
    backdrop: { type: [Boolean, String], default: true },
    // BOffcanvas'da yo'q, yutib yuboramiz
    bgVariant: { type: String, default: undefined },
    shadow: { type: [Boolean, String], default: false },
  },
  setup(props, { attrs, slots }) {
    return () =>
      h(
        _BOffcanvas,
        {
          ...attrs,
          placement: props.right !== false ? "end" : "start",
          backdrop: props.backdrop !== false,
          style: props.width
            ? [attrs.style, { width: props.width }]
            : attrs.style,
        },
        slots,
      )
  },
})

// BIconReply - stub icon component (reply arrow icon)
export const BIconReply = defineComponent({
  name: "BIconReply",
  props: {
    scale: { type: [Number, String], default: 1 },
    fontScale: { type: [Number, String], default: 1 },
    variant: String,
  },
  setup(props) {
    const effectiveScale = Number.parseFloat(props.fontScale || props.scale) || 1
    const size = effectiveScale * 16
    return () =>
      h(
        "svg",
        {
          viewBox: "0 0 16 16",
          width: size,
          height: size,
          fill: "currentColor",
          class: [
            "bi",
            "bi-reply",
            props.variant ? `text-${props.variant}` : "",
          ],
          style: { verticalAlign: "-0.125em" },
        },
        [
          h("path", {
            d: "M6.598 5.013a.144.144 0 0 1 .202.134V6.3a.5.5 0 0 0 .5.5c.667 0 2.013.005 3.3.822.984.624 1.99 1.76 2.595 3.876-1.02-.983-2.185-1.516-3.205-1.799a8.74 8.74 0 0 0-1.921-.306 7.404 7.404 0 0 0-.798.008h-.013l-.005.001h-.001L7.3 9.9l-.05-.498a.5.5 0 0 0-.45.498v1.153a.144.144 0 0 1-.202.134L2.614 8.001a.144.144 0 0 1 0-.268l3.984-2.72zM7.8 10.386c.068 0 .143.003.223.006.434.02 1.034.086 1.7.271 1.326.368 2.896 1.202 3.94 3.08a.5.5 0 0 0 .933-.305c-.464-3.71-1.886-5.662-3.46-6.66-1.245-.79-2.527-.942-3.336-.971v-.66a1.144 1.144 0 0 0-1.767-.96l-3.994 2.72a1.144 1.144 0 0 0 0 2.186l3.994 2.72a1.144 1.144 0 0 0 1.767-.96v-.667z",
          }),
        ],
      )
  },
})

// VBToggle directive - toggles collapse/offcanvas elements
export const VBToggle = {
  mounted(el, binding) {
    const targetId = binding.value || binding.arg
    if (targetId) {
      el.setAttribute("data-bs-toggle", "collapse")
      el.setAttribute("data-bs-target", `#${targetId}`)
    }
  },
  updated(el, binding) {
    const targetId = binding.value || binding.arg
    if (targetId) {
      el.setAttribute("data-bs-target", `#${targetId}`)
    }
  },
}

// VBTooltip directive stub
export const VBTooltip = {
  mounted(el, binding) {
    // Bootstrap 5 uses data-bs-* attributes
    el.setAttribute("data-bs-toggle", "tooltip")
    el.setAttribute("data-bs-placement", binding.value?.placement || "top")
    el.setAttribute(
      "title",
      typeof binding.value === "string"
        ? binding.value
        : binding.value?.title || "",
    )
  },
  updated(el, binding) {
    el.setAttribute(
      "title",
      typeof binding.value === "string"
        ? binding.value
        : binding.value?.title || "",
    )
  },
}

// VBModal directive - triggers modal by ID
// bootstrap-vue-next's BModal uses Bootstrap 5's modal API
// We'll use data attributes and Bootstrap's native modal API
export const VBModal = {
  mounted(el, binding) {
    const modalId = binding.value || binding.arg
    if (modalId) {
      el._vbModalHandler = (e) => {
        e.preventDefault()
        e.stopPropagation()

        // Find the modal element
        const modalElement = document.getElementById(modalId)
        if (modalElement) {
          // Use Bootstrap 5 modal API if available
          if (window.bootstrap && window.bootstrap.Modal) {
            let modal = window.bootstrap.Modal.getInstance(modalElement)
            if (!modal) {
              modal = new window.bootstrap.Modal(modalElement)
            }
            modal.show()
          } else {
            // Fallback: use data attributes to trigger modal
            el.setAttribute("data-bs-toggle", "modal")
            el.setAttribute("data-bs-target", `#${modalId}`)
            // Trigger click which will be handled by Bootstrap
            const clickEvent = new MouseEvent("click", {
              bubbles: true,
              cancelable: true,
            })
            el.dispatchEvent(clickEvent)
          }
        }
      }
      el.addEventListener("click", el._vbModalHandler)
    }
  },
  updated(el, binding) {
    // Re-bind if value changes
    const modalId = binding.value || binding.arg
    if (modalId) {
      if (el._vbModalHandler) {
        el.removeEventListener("click", el._vbModalHandler)
      }
      el._vbModalHandler = (e) => {
        e.preventDefault()
        e.stopPropagation()
        const modalElement = document.getElementById(modalId)
        if (modalElement && window.bootstrap && window.bootstrap.Modal) {
          let modal = window.bootstrap.Modal.getInstance(modalElement)
          if (!modal) {
            modal = new window.bootstrap.Modal(modalElement)
          }
          modal.show()
        }
      }
      el.addEventListener("click", el._vbModalHandler)
    }
  },
  unmounted(el) {
    if (el._vbModalHandler) {
      el.removeEventListener("click", el._vbModalHandler)
      delete el._vbModalHandler
    }
  },
}
